<template>
  <div class="hub-particles">
    <canvas ref="canvas" />
  </div>
</template>

<script>
/* eslint-disable no-mixed-operators */

// defining the particle
class Particle {
  context = null;

  position = {
    // positon on canvas where top-left = 0, 0
    x: 0, // Number
    y: 0, // Number
  };

  // current angle, changes by rotationSpeed per second
  angle = 0;

  // color of particle, ignored if image is used
  color = {
    r: 255,
    g: 255,
    b: 255,
  }; // Object: {r, g, b}

  // opacity from 0 to 1
  opacity = 1; // Number

  // radius of particle, or if image used, scales the image to the size in pixels.
  // For example, a "size" of 5 will scale the image to 5 pixels on the largest side.
  size = 1; // Number

  // optional image to use as particle.
  image = null; // String

  // predefined shape of particle, possible values are:
  // circle
  // square (to be implemented)
  // triangle (to be implemented)
  shape = 'circle'; // String

  // angle of travel in degrees
  // 0 = right
  // 90 = down
  // 180 = left
  // 270 = up
  direction = 0; // Number

  // pixels traveled per second. Calculated with 'direction' to create {x, y} velocity
  speed = 1; // Number

  // degrees per second. Positive is clockwise, negative is counterclockwise
  rotationSpeed = 0; // Number

  constructor({ context, position, color, opacity, size, direction, speed, rotationSpeed, angle, image, shape }) {
    this.context = context;
    this.position = position;
    this.color = color;
    this.opacity = opacity;
    this.size = size;
    this.direction = direction;
    this.speed = speed;
    this.rotationSpeed = rotationSpeed;
    this.angle = angle;
    this.image = image;
    this.shape = shape;
  }

  // Sets the position of the particle by a time factor in seconds, influenced by the direction and
  // speed of the particle.
  updatePosition(timeFactor) {
    // javascript math is inaccurate, but the numbers are close enough for animations
    const xDelta = Math.cos(this.direction) * this.speed * timeFactor;
    const yDelta = Math.sin(this.direction) * this.speed * timeFactor;

    this.position = {
      x: this.position.x + xDelta,
      y: this.position.y + yDelta,
    };
  }

  // Sets the rotation by a time factor in seconds. Influenced by the rotation speed of the particle
  updateRotation(timeFactor) {
    this.angle += this.rotationSpeed * timeFactor;
  }

  // draw particle on particle's canvas context, or on the provided context
  draw(contextArg) {
    const context = contextArg || this.context;

    if (this.image) {
      this.drawImage(context);
    } else if (this.shape === 'circle') {
      this.drawCircle(context);
    } else if (this.shape === 'square') {
      // TODO: not implemented
    } else if (this.shape === 'triangle') {
      // TODO: not implemented
    }
  }

  drawImage(context) {
    // scale to fit, ratio based on bigger side
    const scaleRatio = Math.max(this.image.width, this.image.height) / this.size;
    const scaledWidth = this.image.width / scaleRatio;
    const scaledHeight = this.image.height / scaleRatio;

    // image positioned in center, offset image by half of each side
    const imageXPos = 0 - scaledWidth / 2;
    const imageYPos = 0 - scaledHeight / 2;

    const xPos = this.position.x;
    const yPos = this.position.y;
    context.save();
    context.translate(xPos, yPos);
    context.rotate(Math.PI * (this.angle / 180));
    context.globalAlpha = this.opacity;
    context.drawImage(this.image, imageXPos, imageYPos, scaledWidth, scaledHeight);
    context.restore();
  }

  drawCircle(context) {
    const fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.opacity})`;

    context.beginPath();
    context.arc(this.position.x, this.position.y, this.size, 0, 2 * Math.PI);
    context.fillStyle = fillStyle;
    context.fill();
  }

  // TODO
  // VSCode Vetur doesn't allow # private fields, so getters and setters may not be needed
  // but we can still create them
}

export default {
  name: 'h-particles',
  props: {
    numParticles: {
      type: Number,
      default: 50,
      validator(value) {
        return value > 0;
      },
    },
    density: {
      // higher number means less particles. If not provided, uses numParticles as is
      type: Number,
      default: null,
      validator(value) {
        return value > 0;
      },
    },

    // particle parameters
    particleColor: {
      type: String,
      default: '#FFFFFF',
    },
    particleOpacity: {
      type: Number,
      default: 1,
    },
    minParticleSize: {
      type: Number,
      default: 1,
    },
    maxParticleSize: {
      type: Number,
      default: 2,
    },
    minSpeed: {
      type: Number,
      default: 5,
    },
    maxSpeed: {
      type: Number,
      default: 10,
    },

    // rotation, only works with images (maybe later with non-circle shapes)
    minRotationSpeed: {
      // degrees per second
      type: Number,
      default: 0,
    },
    maxRotationSpeed: {
      // degrees per second
      type: Number,
      default: 0,
    },
    rotationDisabled: {
      // disables rotation speed and keeps images upright
      type: Boolean,
      default: false,
    },

    // min/max values to clamp particle amounts
    minParticles: {
      type: Number,
      default: 10,
    },
    maxParticles: {
      type: Number,
      default: 50,
    },

    // Image data
    image: {
      type: String,
      default: null,
    },
    images: {
      // overrides single image
      type: Array,
      default: null,
    },

    // modifier that works with images, to render only one of each image in the
    // image array
    oneParticlePerImage: {
      type: Boolean,
      default: false,
    },

    // line parameters
    lineColor: {
      type: String,
      default: '#FFFFFF',
      validator(value) {
        // must be either hex or rgb, not rgba
        return (
          /^#?(([a-f\d]{1}){3}|([a-f\d]{2}){3}){1}$/i.test(value) || /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i.test(value)
        );
      },
    },
    lineOpacity: {
      type: Number,
      default: 0.8,
    },
    lineMaxLength: {
      type: Number,
      default: null,
    },
    lineThickness: {
      type: Number,
      default: 2,
    },

    // Use to stop particle component from updating on any change. Fixes an issue in some
    // pages where any page interacting causes a refresh of particles.
    preventUpdate: {
      type: Boolean,
      defualt: false,
    },
  },
  data() {
    return {
      particleList: [],

      canvasSize: {
        height: 0,
        width: 0,
      },

      context: null,

      previousTime: null,

      animationFrame: null,
    };
  },
  computed: {
    computedNumParticles() {
      return this.density ? ((this.canvasArea / 1000) * this.numParticles) / this.density : this.numParticles;
    },
    limitedNumParticles() {
      return this.density
        ? Math.min(this.maxParticles, Math.max(this.minParticles, this.computedNumParticles))
        : this.computedNumParticles;
    },
    canvasArea() {
      return this.canvasSize.height * this.canvasSize.width;
    },
    imageObject() {
      // convertss single image string to object to be reused
      if (this.image) {
        const imageObject = new Image();
        imageObject.src = this.image;
        return imageObject;
      }
      return null;
    },
    imageObjects() {
      // converts multiple images into objects to be reused
      if (this.images) {
        return this.images.map((image) => {
          const imageObject = new Image();
          imageObject.src = image;
          return imageObject;
        });
      }
      return null;
    },
  },
  mounted() {
    // initial setup
    this.context = this.$refs.canvas.getContext('2d');
    this.resetMatrix();

    this.animationFrame = requestAnimationFrame(this.animateFrame);
  },

  beforeUnmount() {
    cancelAnimationFrame(this.animationFrame);
  },

  methods: {
    randomNumberInRange(min, max) {
      return Math.random() * (max - min) + min;
    },
    randomPositiveOrNegative() {
      return Math.random() * 2 > 1 ? 1 : -1;
    },
    resetMatrix() {
      if (!this.preventUpdate || !this.updated) {
        this.$refs.canvas.height = this.$refs.canvas.parentElement.clientHeight;
        this.$refs.canvas.width = this.$refs.canvas.parentElement.clientWidth;

        this.canvasSize = {
          // stored reactive data for computed values
          height: this.$refs.canvas.height,
          width: this.$refs.canvas.width,
        };

        this.particleList = this.createParticleList();

        this.updated = true;
      }
    },

    createParticleList() {
      if (this.images && this.oneParticlePerImage) {
        return this.imageObjects.map((obj) => this.createRandomParticle({ image: obj }));
      }

      // priority: images > image > shape
      return Array.from({ length: Math.floor(this.limitedNumParticles) }, () => {
        if (this.images) {
          return this.createRandomParticle({
            image: this.imageObjects[Math.floor(Math.random() * this.imageObjects.length)],
          });
        }
        if (this.image) {
          return this.createRandomParticle({
            image: this.imageObject,
          });
        }
        return this.createRandomParticle({
          shape: 'circle', // hardcoded circle since others aren't implemented
        });
      });
    },

    /**
     * Create a single particle with randomized data based on props.
     * @param {Object} options an options object
     * @param {String} options.image Image to render as a particle
     * @param {String} options.shape A shape to be generated. Not used if image exists.
     *   - Possible shapes: 'circle', 'square' (not implemented), 'triangle' (not implemented).
     * @returns {Particle} A filled article object
     */
    createRandomParticle({ image, shape }) {
      return new Particle({
        context: this.context,
        position: this.generateRandomPosition(),
        // color: this.convertColorToObj(this.particleColor),
        opacity: this.particleOpacity,
        size: this.randomNumberInRange(this.minParticleSize, this.maxParticleSize),
        direction: this.randomNumberInRange(0, 360),
        speed: this.randomNumberInRange(this.minSpeed, this.maxSpeed),
        angle: this.rotationDisabled ? 0 : this.randomNumberInRange(0, 360),

        rotationSpeed: this.rotationDisabled
          ? 0
          : this.randomNumberInRange(this.minRotationSpeed, this.maxRotationSpeed) * this.randomPositiveOrNegative(),

        image,
        shape,
      });
    },

    generateRandomPosition() {
      return {
        x: this.randomNumberInRange(0, this.canvasSize.width),
        y: this.randomNumberInRange(0, this.canvasSize.height),
      };
    },

    animateFrame() {
      const currentTime = Date.now();
      const deltaTime = this.previousTime === null ? 0 : currentTime - this.previousTime;
      this.previousTime = currentTime;

      // make sure an element resize changes the canvas (such as from 'display: none' having 0 height and width)
      this.checkForResize();

      // remove previous frame before redrawing
      this.context.clearRect(0, 0, this.context.canvas.width, this.context.canvas.height);

      // start updating particles, and draw each on canvas
      this.particleList.forEach((particle) => {
        this.updateParticle(particle, deltaTime);
        this.drawParticle(particle);
      });

      // draw lines between particles on canvas only if line length is present and above 0
      // to prevent unneeded expensive computation
      if (this.lineMaxLength !== null && this.lineMaxLength > 0) {
        this.drawLines();
      }
      this.animationFrame = requestAnimationFrame(this.animateFrame);
    },

    updateParticle(ptcl, deltaTime) {
      const particle = ptcl;
      particle.updatePosition(deltaTime / 1000); // factor of a second

      // loop around canvas if edges hit
      // loop if center position with half of size passed boundary (so it doesn't jump when half the
      // particle is in view)
      // add/subtract sets the canvas width with full size to boundary, so particle is fully enclosed
      // past boundaries before jumping
      // (plus half-size for < 0, minus half-size for > canvas size)
      if (particle.position.x + particle.size / 2 < 0) {
        particle.position.x += this.context.canvas.width + particle.size;
      } else if (particle.position.x - particle.size / 2 > this.context.canvas.width) {
        particle.position.x -= this.context.canvas.width + particle.size;
      }
      if (particle.position.y + particle.size / 2 < 0) {
        particle.position.y += this.context.canvas.height + particle.size;
      } else if (particle.position.y - particle.size / 2 > this.context.canvas.height) {
        particle.position.y -= this.context.canvas.height + particle.size;
      }

      // update rotation as well
      particle.updateRotation(deltaTime / 1000); // factor of a second

      // keep rotation from 0 to 360 for simplicity
      if (particle.angle < 0) {
        particle.angle += 360;
      }
      if (particle.angle > 360) {
        particle.angle -= 360;
      }
    },

    drawParticle(particle) {
      particle.draw(this.context);
    },

    drawLines() {
      // for each 2 particles, check if within distance, then draw line if within min distance
      for (let i = 0; i < this.particleList.length; i += 1) {
        // start j at i + 1 to prevent repeating pairs
        for (let j = i + 1; j < this.particleList.length; j += 1) {
          const lineLength = this.getDistance(this.particleList[i], this.particleList[j]);
          if (lineLength <= this.lineMaxLength) {
            this.drawLine(
              this.particleList[i],
              this.particleList[j],
              this.lineColor,
              ((this.lineMaxLength - lineLength) / this.lineMaxLength) * this.lineOpacity,
              this.lineThickness
            );
          }
        }
      }
    },

    drawLine(p1, p2, color, opacity, thickness) {
      const rgbColor = this.convertColorToObj(color);
      const strokeStyle = `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, ${opacity}`;

      this.context.beginPath();
      this.context.moveTo(p1.position.x, p1.position.y);
      this.context.lineTo(p2.position.x, p2.position.y);
      this.context.lineWidth = thickness;
      this.context.strokeStyle = strokeStyle;
      this.context.stroke();
    },

    getDistance(p1, p2) {
      return Math.sqrt((p1.position.x - p2.position.x) ** 2 + (p1.position.y - p2.position.y) ** 2);
    },

    checkForResize() {
      const detectedSize = {
        height: this.$refs.canvas.parentElement.clientHeight,
        width: this.$refs.canvas.parentElement.clientWidth,
      };

      if (this.canvasSize.height !== detectedSize.height || this.canvasSize.width !== detectedSize.width) {
        this.resetMatrix();
      }
    },

    // color is eitehr hex ( #FFFFFF ) or rgb ( rgb(255, 255, 255) ) string
    convertColorToObj(color) {
      const colorObj = this.$hutils.convertColorToObj(color);
      // if color can't be detected, default to white
      return colorObj || { r: 255, g: 255, b: 255 };
    },
  },
  watch: {
    // watch on all props that require new list to be created
    numParticles() {
      this.resetMatrix();
    },
    density() {
      this.resetMatrix();
    },
    maxParticles() {
      this.resetMatrix();
    },
    minParticles() {
      this.resetMatrix();
    },
    particleColor() {
      this.resetMatrix();
    },
    particleOpacity() {
      this.resetMatrix();
    },
    minParticleSize() {
      this.resetMatrix();
    },
    maxParticleSize() {
      this.resetMatrix();
    },
    minSpeed() {
      this.resetMatrix();
    },
    maxSpeed() {
      this.resetMatrix();
    },
    minRotationSpeed() {
      this.resetMatrix();
    },
    maxRotationSpeed() {
      this.resetMatrix();
    },
    rotationDisabled() {
      this.resetMatrix();
    },
    image() {
      this.resetMatrix();
    },
    images() {
      this.resetMatrix();
    },
    oneParticlePerImage() {
      this.resetMatrix();
    },
  },
};
</script>

<style scoped>
.hub-particles,
canvas {
  position: absolute;
  top: 0%;
  left: 0%;
  height: 100%;
  width: 100%;
}
</style>
